import React, { useContext, useEffect } from "react";

import { faStop } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AppContext } from "~/common";
import { confirm } from "~/lib/prompts";
import MeetingPhaseContext, { type CopilotStatus } from "~/stream/MeetingPhaseContext";

import { ControlGroupButtonTooltip, ControlGroupIconButton } from "./SharedComponents";

import styles from "./InMeetingControls.module.css";

type CopilotStatusType = Extract<CopilotStatus["type"], "starting" | "waiting" | "recording" | "paused">;

export default function StopRecordingButton({ copilotStatus }: { copilotStatus: CopilotStatusType }) {
	const { stopRecording, copilotStatus: copilotStatusContext, creator } = useContext(MeetingPhaseContext);
	const { user } = useContext(AppContext);

	const onClick = React.useCallback(async () => {
		switch (copilotStatus) {
			case "starting":
			case "waiting": {
				const confirmTitle = "Cancel the Note Taker?";
				const confirmText = (
					<p>
						Cancelling will prevent the Note Taker from joining the meeting and it will not be recorded. Are you sure
						you want to cancel the Note Taker?
					</p>
				);

				if (
					!(await confirm(confirmText, {
						yesText: "Cancel the Note Taker",
						noText: "Go back",
						yesProps: { variant: "destructive" },
						title: confirmTitle,
					}))
				) {
					return;
				}
				break;
			}
			case "recording":
			case "paused": {
				const confirmText = <p>The meeting recording and transcript up until this point will be saved.</p>;
				if (
					!(await confirm(confirmText, {
						yesText: "Stop",
						yesProps: { variant: "primary" },
						title: "Stop recording?",
					}))
				) {
					return;
				}
				break;
			}
		}

		stopRecording();
	}, [stopRecording, copilotStatus]);

	useEffect(() => {
		// Check if we have the desktop recorder
		if (!window.desktopRecorder || !window.desktopRecorder.registerPromptToStopRecordingListener) return;
		// Check if we are doing a local recording and this user is the creator
		// Note: Breaking into multiple if statements for readability
		if (copilotStatusContext.type !== "recording" && copilotStatusContext.type !== "paused") return;
		if (copilotStatusContext.provider !== "CLIENT_SIDE") return;
		if (creator?.id !== user?.id) return;
		window.desktopRecorder.registerPromptToStopRecordingListener(onClick);
		return () => {
			window.desktopRecorder?.registerPromptToStopRecordingListener(undefined);
		};
	}, [onClick, copilotStatusContext, creator, user]);

	return (
		<ControlGroupButtonTooltip
			content={copilotStatus === "starting" || copilotStatus === "waiting" ? "Cancel" : "Stop"}
		>
			<ControlGroupIconButton onClick={onClick}>
				<FontAwesomeIcon icon={faStop} className={styles.squareIcon} />
			</ControlGroupIconButton>
		</ControlGroupButtonTooltip>
	);
}
